import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, Paper, TextField, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getUserHousingPreference, getLookup } from '../../../network/apis';

const FALLBACK_LABELS = {
  // OWNERSHIP_PURPOSE / TRACK
  INVESTMENT: { ar: 'استثمار', en: 'Investment' },
  PERSONAL: { ar: 'سكن شخصي', en: 'Personal' },
  FAMILY: { ar: 'سكن عائلي', en: 'Family' },
  HOUSING: { ar: 'سكن عائلي', en: 'Family' },
  BOTH: { ar: 'كليهما (استثمار وسكن عائلي)', en: 'Both (Investment & Personal)' },

  // INVESTMENT_TYPE
  RENTAL_YIELD: { ar: 'عائد إيجاري', en: 'Rental Yield' },
  CAPITAL_APPRECIATION: { ar: 'نمو رأسمالي (إعادة بيع)', en: 'Capital Appreciation (Resale)' },
  CAPITAL_GROWTH: { ar: 'نمو رأسمالي (إعادة بيع)', en: 'Capital Appreciation (Resale)' },
  RESALE: { ar: 'نمو رأسمالي (إعادة بيع)', en: 'Capital Appreciation (Resale)' },

  // UNIT_TYPE
  APARTMENT: { ar: 'شقة', en: 'Apartment' },
  VILLA: { ar: 'فلة', en: 'Villa' },
  FLOOR: { ar: 'دور', en: 'Floor' },
  TOWNHOUSE: { ar: 'تاون هاوس', en: 'Townhouse' },
  COMMERCIAL: { ar: 'تجاري', en: 'Commercial' },
  LAND: { ar: 'أرض', en: 'Land' },

  // UNITS_COUNT / NUMBERS
  ONE: { ar: '1', en: '1' },
  TWO: { ar: '2', en: '2' },
  THREE: { ar: '3', en: '3' },
  FOUR: { ar: '4', en: '4' },
  FIVE: { ar: '5', en: '5' },
  FIVE_OR_MORE: { ar: '5 وأكثر', en: '5+' },
  SIX: { ar: '6', en: '6' },
  SEVEN: { ar: '7', en: '7' },
};

const formatDate = (dateStr, isRtl) => {
  if (!dateStr) return '---------';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const SectionCard = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2.5, md: 3 },
      borderRadius: '16px',
      border: '1px solid #E5E8EB',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box
        sx={{
          width: 4,
          height: 20,
          backgroundColor: '#27A376',
          borderRadius: '4px',
        }}
      />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', fontSize: '16px' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const ReadonlyField = ({ label, value, isRtl }) => (
  <Grid item xs={12} sm={6}>
    <Typography
      variant="subtitle2"
      sx={{
        mb: 1,
        color: '#637381',
        fontWeight: 500,
        fontSize: '13px',
        textAlign: isRtl ? 'right' : 'left',
      }}
    >
      {label}
    </Typography>
    <TextField
      fullWidth
      disabled
      value={value ?? '---------'}
      variant="outlined"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px',
          backgroundColor: '#F9FAFB',
          minHeight: '48px',
          '& fieldset': {
            borderColor: '#E5E8EB',
          },
          '&:hover fieldset': {
            borderColor: '#D1D5DB',
          },
        },
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#212B36',
          fontWeight: 500,
          fontSize: '14px',
          padding: '12px 14px',
          textAlign: isRtl ? 'right' : 'left',
        },
      }}
    />
  </Grid>
);

ReadonlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRtl: PropTypes.bool,
};

export default function HousingPreferences({ user, userId }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const activeUserId = userId || user?.userId || user?.id;

  const [loading, setLoading] = useState(true);
  const [prefsData, setPrefsData] = useState(null);
  const [lookUpData, setLookUpData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [prefsRes, lookupRes] = await Promise.allSettled([
          activeUserId ? getUserHousingPreference(activeUserId) : Promise.reject(new Error('No user ID')),
          getLookup('OWNERSHIP_PURPOSE,FAMILY_MEMBERS,INVESTMENT_TYPE,EXPECTED_SALARY,BUDGET,REQUIRED_ROOM_COUNT,UNITS_COUNT,UNIT_TYPE'),
        ]);

        if (isMounted) {
          if (prefsRes.status === 'fulfilled') {
            const data = prefsRes.value?.data?.DATA || prefsRes.value?.data?.data || prefsRes.value?.data;
            setPrefsData(data);
          } else {
            console.error('Failed to fetch user housing preferences:', prefsRes.reason);
            setPrefsData(null);
          }

          if (lookupRes.status === 'fulfilled') {
            const lData = lookupRes.value?.data?.DATA || lookupRes.value?.data?.data || lookupRes.value?.data;
            setLookUpData(lData);
          }
        }
      } catch (err) {
        console.error('Error fetching housing preferences data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [activeUserId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[1, 2, 3].map((item) => (
          <Paper key={item} sx={{ p: 3, borderRadius: '16px', border: '1px solid #E5E8EB' }}>
            <Skeleton variant="text" width={160} height={28} sx={{ mb: 2 }} />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="rounded" height={48} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="rounded" height={48} />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Box>
    );
  }

  if (
    !prefsData ||
    typeof prefsData !== 'object' ||
    Object.keys(prefsData).length === 0 ||
    (!prefsData.id &&
      !prefsData.ownershipPurpose &&
      !prefsData.year &&
      prefsData.familyTrack === undefined &&
      prefsData.investmentTrack === undefined)
  ) {
    return (
      <Box mt={2} width="100%">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: '16px',
            border: '1px dashed #E0E0E0',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Typography color="text.secondary" variant="body1">
            {t('users.noHousingPreferences') ||
              (isRtl
                ? 'لا توجد تفضيلات إسكانيّة مسجّلة لهذا المستخدم.'
                : 'No housing preferences recorded for this user.')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const getLookupLabel = (lookupKey, rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '---------';
    }

    const list = lookUpData?.[lookupKey] || lookUpData?.DATA?.[lookupKey];
    if (Array.isArray(list)) {
      const item = list.find(
        (i) =>
          String(i.value).toLowerCase() === String(rawValue).toLowerCase() ||
          String(i.code).toLowerCase() === String(rawValue).toLowerCase()
      );
      if (item) {
        if (isRtl) {
          return item.labelAr || item.label || item.nameAr || item.name || item.valueAr || item.value;
        }
        return item.labelEn || item.label || item.nameEn || item.name || item.valueEn || item.value;
      }
    }

    if (FALLBACK_LABELS[rawValue]) {
      return isRtl ? FALLBACK_LABELS[rawValue].ar : FALLBACK_LABELS[rawValue].en;
    }

    return rawValue;
  };

  const getRegionName = (regionObj, regionId, fallbackRegion) => {
    const reg = regionObj || fallbackRegion;
    if (reg && typeof reg === 'object') {
      return (isRtl ? (reg.nameAr || reg.name) : (reg.nameEn || reg.name)) || '---------';
    }
    return regionId || '---------';
  };

  const getCityName = (cityObj, cityId, fallbackCity) => {
    const ct = cityObj || fallbackCity;
    if (ct && typeof ct === 'object') {
      return (isRtl ? (ct.nameAr || ct.name) : (ct.nameEn || ct.name)) || '---------';
    }
    return cityId || '---------';
  };

  const formatAge = (ageVal) => {
    if (ageVal === null || ageVal === undefined || ageVal === '') return '---------';
    return isRtl ? `${ageVal} سنة` : `${ageVal} years`;
  };

  // Track activity determination
  const isInvestmentTrackActive =
    String(prefsData?.investmentTrack) === '1' ||
    prefsData?.investmentTrack === true ||
    prefsData?.investmentTrack === 1;

  const isFamilyTrackActive =
    String(prefsData?.familyTrack) === '1' ||
    prefsData?.familyTrack === true ||
    prefsData?.familyTrack === 1;

  // Preferred Track label calculation
  let trackLabel = '---------';
  if (isInvestmentTrackActive && isFamilyTrackActive) {
    trackLabel = isRtl ? 'المسارين (استثمار وسكن عائلي)' : 'Both Tracks (Investment & Personal)';
  } else if (isInvestmentTrackActive) {
    trackLabel = isRtl ? 'مسار الاستثمار' : 'Investment Track';
  } else if (isFamilyTrackActive) {
    trackLabel = isRtl ? 'مسار السكن الشخصي' : 'Personal Housing Track';
  } else if (prefsData?.track) {
    trackLabel = getLookupLabel('OWNERSHIP_PURPOSE', prefsData.track);
  } else if (prefsData?.ownershipPurpose) {
    trackLabel = getLookupLabel('OWNERSHIP_PURPOSE', prefsData.ownershipPurpose);
  }

  // Values extraction with fallbacks
  // Investment track values
  const investmentType = prefsData?.investmentType;
  const investmentAge = prefsData?.investmentAge ?? prefsData?.age;
  const investmentExpectedSalary = prefsData?.investmentExpectedSalary ?? prefsData?.expectedSalary;
  const investmentBudget = prefsData?.investmentBudget ?? prefsData?.budget;
  const investmentUnitsCount = prefsData?.investmentUnitsCount ?? prefsData?.unitsCount;
  const investmentUnitType = prefsData?.investmentUnitType ?? prefsData?.unitType;
  const investmentRegionName = getRegionName(
    prefsData?.investmentRegion,
    prefsData?.investmentRegionId,
    prefsData?.region
  );
  const investmentCityName = getCityName(
    prefsData?.investmentCity,
    prefsData?.investmentCityId,
    prefsData?.city
  );

  // Family track values
  const familyMembers = prefsData?.familyMembers;
  const familyAge = prefsData?.familyAge ?? prefsData?.age;
  const familyExpectedSalary = prefsData?.familyExpectedSalary ?? prefsData?.expectedSalary;
  const familyBudget = prefsData?.familyBudget ?? prefsData?.budget;
  const familyRoomCount = prefsData?.familyRoomCount ?? prefsData?.requiredRoomCount;
  const familyUnitType = prefsData?.familyUnitType ?? prefsData?.unitType;
  const familyRegionName = getRegionName(
    prefsData?.familyRegion,
    prefsData?.familyRegionId,
    prefsData?.region
  );
  const familyCityName = getCityName(
    prefsData?.familyCity,
    prefsData?.familyCityId,
    prefsData?.city
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 1. البيانات الأساسية */}
      <SectionCard title={t('housingPreferences.basicData') || (isRtl ? 'البيانات الأساسية' : 'Basic Data')}>
        <Grid container spacing={2.5}>
          <ReadonlyField
            label={t('housingPreferences.expectedOwnershipYear') || (isRtl ? 'سنة التملك المتوقعة' : 'Expected Ownership Year')}
            value={prefsData?.year || '---------'}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.mainTrackGoal') || (isRtl ? 'المسار / الهدف الرئيسي من تملك العقار' : 'Main Track / Goal')}
            value={trackLabel}
            isRtl={isRtl}
          />
        </Grid>
      </SectionCard>

      {/* 2. مسار الاستثمار */}
      <SectionCard title={t('housingPreferences.investmentTrack') || (isRtl ? 'مسار الاستثمار' : 'Investment Track')}>
        <Grid container spacing={2.5}>
          <ReadonlyField
            label={t('housingPreferences.investmentType') || (isRtl ? 'نوع الاستثمار' : 'Investment Type')}
            value={getLookupLabel('INVESTMENT_TYPE', investmentType)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.age') || (isRtl ? 'العمر' : 'Age')}
            value={formatAge(investmentAge)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.expectedMonthlyIncome') || (isRtl ? 'الدخل الشهري المتوقع' : 'Expected Monthly Income')}
            value={getLookupLabel('EXPECTED_SALARY', investmentExpectedSalary)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.investmentBudget') || (isRtl ? 'الميزانية الاستثمارية' : 'Investment Budget')}
            value={getLookupLabel('BUDGET', investmentBudget)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.targetUnitsCount') || (isRtl ? 'عدد الوحدات المستهدفة' : 'Target Units Count')}
            value={getLookupLabel('UNITS_COUNT', investmentUnitsCount)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.preferredUnitType') || (isRtl ? 'نوع الوحدة المفضل' : 'Preferred Unit Type')}
            value={getLookupLabel('UNIT_TYPE', investmentUnitType)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.region') || (isRtl ? 'المنطقة' : 'Region')}
            value={investmentRegionName}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.city') || (isRtl ? 'المدينة' : 'City')}
            value={investmentCityName}
            isRtl={isRtl}
          />
        </Grid>
      </SectionCard>

      {/* 3. مسار السكن الشخصي */}
      <SectionCard title={t('housingPreferences.familyHousingTrack') || (isRtl ? 'مسار السكن الشخصي' : 'Personal Housing Track')}>
        <Grid container spacing={2.5}>
          <ReadonlyField
            label={t('housingPreferences.expectedFamilyMembers') || (isRtl ? 'عدد أفراد الأسرة المتوقع' : 'Expected Family Members')}
            value={getLookupLabel('FAMILY_MEMBERS', familyMembers)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.age') || (isRtl ? 'العمر' : 'Age')}
            value={formatAge(familyAge)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.expectedSalary') || (isRtl ? 'الراتب المتوقع' : 'Expected Salary')}
            value={getLookupLabel('EXPECTED_SALARY', familyExpectedSalary)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.housingBudget') || (isRtl ? 'الميزانية لشراء السكن' : 'Housing Budget')}
            value={getLookupLabel('BUDGET', familyBudget)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.requiredRoomsCount') || (isRtl ? 'عدد الغرف المطلوبة' : 'Required Rooms Count')}
            value={getLookupLabel('REQUIRED_ROOM_COUNT', familyRoomCount)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.preferredUnitType') || (isRtl ? 'نوع الوحدة المفضل' : 'Preferred Unit Type')}
            value={getLookupLabel('UNIT_TYPE', familyUnitType)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.region') || (isRtl ? 'المنطقة' : 'Region')}
            value={familyRegionName}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.city') || (isRtl ? 'المدينة' : 'City')}
            value={familyCityName}
            isRtl={isRtl}
          />
        </Grid>
      </SectionCard>

      {/* 4. السجل الزمني */}
      <SectionCard title={t('housingPreferences.timelineRecord') || (isRtl ? 'السجل الزمني' : 'Timeline Record')}>
        <Grid container spacing={2.5}>
          <ReadonlyField
            label={t('housingPreferences.creationDate') || (isRtl ? 'تاريخ الإنشاء' : 'Creation Date')}
            value={formatDate(prefsData?.creationDate, isRtl)}
            isRtl={isRtl}
          />
          <ReadonlyField
            label={t('housingPreferences.lastUpdateDate') || (isRtl ? 'تاريخ آخر تحديث' : 'Last Update Date')}
            value={formatDate(prefsData?.lastUpdateDate, isRtl)}
            isRtl={isRtl}
          />
        </Grid>
      </SectionCard>
    </Box>
  );
}

HousingPreferences.propTypes = {
  user: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
